import { authGuard } from '@/core/guards/auth.guard';
import { captureDashboardGuard } from '@/core/guards/capture-dashboard.guard';
import { CameraGroupGuard } from '@/core/guards/camera-group.guard';
import { CameraStatusGuard } from '@/core/guards/camera-status.guard';
import { CreateCameraGuard } from '@/core/guards/create-camera.guard';
import { HorusGuard } from '@/core/guards/horus.guard';
import { IntegrationsGovGuard } from '@/core/guards/integrations-gov.guard';
import { MoniIntegrationGuard } from '@/core/guards/moni-integration.guard';
import { ServicePackagesGuard } from '@/core/guards/service-packages.guard';
import { usageMetricsGuard } from '@/core/guards/usage-metrics.guard';
import { WhatsappIntegrationGuard } from '@/core/guards/whatsapp-integration.guard';
import { MeResolver } from '@/core/resolvers/me.resolver';
import { PermissionsService } from '@/core/services/permissions/permissions.service';
import { catchError, of, timeout } from 'rxjs';
import { AuthLayout } from '@/layout/components/app.authlayout';
import { AppLayout } from '@/layout/components/app.layout';
import { Notfound } from '@/pages/notfound/notfound';
import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { environment } from './environments/environment';
import { FeatureEditGuard } from '@/core/guards/feature-edit.guard';
import { LprBlacklistGuard } from '@/core/guards/lpr-blacklist.guard';
import { logoutGuard } from '@/core/guards/logout.guard';
import { MenuPermissionGuard } from '@/core/guards/menu-permission.guard';

export const devOnlyGuard = () =>
  environment.devTools || inject(Router).createUrlTree(['/']);

const devRoutes: Routes = environment.production
  ? []
  : [
      {
        path: 'dev/toast-debug',
        loadComponent: () =>
          import('./app/core/components/alert-toast/debug/alert-toast.debug.component').then(
            (m) => m.AlertToastDebugPanelComponent,
          ),
        canActivate: [devOnlyGuard],
      },
    ];
export const appRoutes: Routes = [
  ...devRoutes,
  {
    path: '',
    component: AppLayout,
    children: [
      {
        path: 'configuracoes/dashboard-capturas',
        canActivate: [authGuard, captureDashboardGuard],
        loadComponent: () =>
          import('./app/features/capture-dashboard/capture-dashboard.component').then(
            (component) => component.CaptureDashboardComponent,
          ),
        data: { breadcrumb: 'Dashboard de Capturas LPR e Facial' },
      },
      {
        path: '',
        redirectTo: 'minhas-cameras',
        pathMatch: 'full',
      },
      {
        path: 'minhas-cameras',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./app/features/home/home.component').then(
            (c) => c.HomeComponent,
          ),
        data: { breadcrumb: 'Minhas Câmeras' },
        resolve: {
          me: () =>
            inject(PermissionsService)
              .resolveMe()
              .pipe(
                timeout(8000),
                catchError(() => of(null)),
              ),
        },
      },
      {
        path: 'minhas-cameras/cameras-dashboard',
        canActivate: [authGuard, CameraStatusGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/cameras-dashboard/cameras-dashboard.component').then(
            (c) => c.CamerasDashboardComponent,
          ),
        data: {
          breadcrumb: 'Dashboard de Câmeras',
          menuPermission: 'can_see_camera_menu',
        },
        resolve: { me: MeResolver },
      },
      {
        path: 'login',
        loadComponent: () =>
          import('./app/auth/auth.component').then((c) => c.AuthComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./app/auth/components/forgot-password/forgot-password.component').then(
            (c) => c.ForgotPasswordComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./app/auth/components/reset-password/reset-password.component').then(
            (c) => c.ResetPasswordComponent,
          ),
      },
      {
        path: 'confirm-email',
        loadComponent: () =>
          import('./app/auth/components/confirm-email/confirm-email.component').then(
            (c) => c.ConfirmEmailComponent,
          ),
      },
      {
        path: 'confirm-login',
        loadComponent: () =>
          import('./app/auth/components/confirm-login/confirm-login.component').then(
            (c) => c.ConfirmLoginComponent,
          ),
      },
      {
        path: 'horus',
        canActivate: [authGuard, HorusGuard],
        loadChildren: () =>
          import('./app/features/horus/horus.routes').then(
            (m) => m.HorusRoutes,
          ),
      },
      {
        path: 'central-downloads',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./app/features/file-processing/file-processing-center-page.component').then(
            (c) => c.FileProcessingCenterPageComponent,
          ),
        data: { breadcrumb: 'file_processing.breadcrumb' },
      },
      {
        path: 'notificacoes/detalhes',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./app/features/home/components/home-notificacoes-list/home-notificacoes-list.component').then(
            (c) => c.HomeNotificacoesListComponent,
          ),
        data: { breadcrumb: 'Notificações' },
      },
      {
        path: 'cadastro-camera',
        canActivate: [authGuard, CreateCameraGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/camera-registration/camera-registration.component').then(
            (c) => c.CameraRegistrationComponent,
          ),
        data: {
          breadcrumb: 'Cadastro de Câmera',
          menuPermission: 'can_see_camera_menu',
        },
        resolve: { me: MeResolver },
      },

      {
        path: 'facial-registration',
        loadChildren: () =>
          import('./app/features/facial-registration/facial-registration.routes').then(
            (m) => m.facialRegistrationRoutes,
          ),
      },
      {
        path: 'register-face',
        redirectTo: 'facial-registration/register',
        pathMatch: 'full',
      },

      {
        path: 'compare-face',
        canActivate: [authGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/facial-comparison-list/facial-comparison-list.component').then(
            (c) => c.FacialComparisonListComponent,
          ),
        data: {
          breadcrumb: 'Comparação de Face',
          menuPermission: 'can_see_facial_menu',
        },
        resolve: { me: MeResolver },
      },
      {
        path: 'face-not-identified',
        canActivate: [authGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/facial-not-identified-list/facial-not-identified-list.component').then(
            (c) => c.FacialNotIdentifiedComponent,
          ),
        data: {
          breadcrumb: 'Faces não identificadas',
          menuPermission: 'can_see_facial_menu',
        },
        resolve: { me: MeResolver },
      },
      {
        path: 'face-low-quality',
        canActivate: [authGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/facial-low-quality/facial-low-quality.component').then(
            (c) => c.FacialLowQualityComponent,
          ),
        data: {
          breadcrumb: 'Faces Desqualificadas',
          menuPermission: 'can_see_facial_menu',
        },
        resolve: { me: MeResolver },
      },

      {
        path: 'users',
        loadChildren: () =>
          import('./app/features/users/users.routes').then(
            (m) => m.usersRoutes,
          ),
      },

      {
        path: 'group-management',
        loadChildren: () =>
          import('./app/features/group-management/group-management.routes').then(
            (m) => m.groupManagementRoutes,
          ),
      },

      {
        path: 'group-cameras',
        canActivate: [authGuard, CameraGroupGuard],
        data: { breadcrumb: 'camera_groups.breadcrumb' },
        loadChildren: () =>
          import('./app/features/camera-groups/camera-groups.routes').then(
            (m) => m.cameraGroupsRoutes,
          ),
      },

      {
        path: 'permission-levels',
        canActivate: [authGuard, FeatureEditGuard],
        data: {
          breadcrumb: 'permission_levels.breadcrumb',
          editPermission: 'can_see_permission_levels_tab',
          redirectTo: '/minhas-cameras',
        },
        loadChildren: () =>
          import('./app/features/permission-levels/permission-levels.routes').then(
            (m) => m.permissionLevelsRoutes,
          ),
      },

      {
        path: 'group-person',
        loadChildren: () =>
          import('./app/features/group-person/group-person.routes').then(
            (m) => m.groupPersonRoutes,
          ),
      },

      {
        path: 'configuracoes/gerenciamento/pacotes-servico',
        canActivate: [authGuard, ServicePackagesGuard],
        loadChildren: () =>
          import('./app/features/service-packages/service-packages.routes').then(
            (m) => m.servicePackagesRoutes,
          ),
      },

      {
        path: 'configuracoes/gerenciamento/integracao-whatsapp',
        canActivate: [authGuard, WhatsappIntegrationGuard],
        loadChildren: () =>
          import('./app/features/whatsapp-integration/whatsapp-integration.routes').then(
            (m) => m.whatsappIntegrationRoutes,
          ),
      },
      {
        path: 'configuracoes/gerenciamento/metricas-uso',
        canActivate: [authGuard, usageMetricsGuard],
        loadChildren: () =>
          import('./app/features/usage-metrics-dashboard/usage-metrics-dashboard.routes').then(
            (m) => m.usageMetricsDashboardRoutes,
          ),
      },

      {
        path: 'facial-notification-rules',
        canActivate: [authGuard, MenuPermissionGuard],
        data: {
          breadcrumb: 'facial_notification_rules.breadcrumb',
          menuPermission: 'can_see_facial_menu',
        },
        loadChildren: () =>
          import('./app/features/facial-notification-rules/facial-notification-rules.routes').then(
            (m) => m.facialNotificationRulesRoutes,
          ),
      },

      // ── Mosaic ───────────────────────────────────────────────────────────
      {
        path: 'mosaic',
        loadChildren: () =>
          import('./app/features/mosaic/mosaic.routes').then(
            (m) => m.mosaicRoutes,
          ),
      },
      {
        path: 'mosaic-view/:id',
        redirectTo: 'mosaic/view/:id',
        pathMatch: 'full',
      },
      {
        path: 'create-speed-mosaic',
        redirectTo: 'mosaic/create-speed',
        pathMatch: 'full',
      },
      {
        path: 'create-by-map',
        redirectTo: 'mosaic/create-by-map',
        pathMatch: 'full',
      },

      // ── Automação ──────────────────────────────────────────────────────
      {
        path: 'automacao',
        canActivate: [authGuard],
        loadChildren: () =>
          import('./app/features/automation/automation.routes').then(
            (m) => m.automationRoutes,
          ),
      },

      // ── Patrol ───────────────────────────────────────────────────────────
      {
        path: 'patrol',
        loadChildren: () =>
          import('./app/features/patrol/patrol.routes').then(
            (m) => m.patrolRoutes,
          ),
      },
      {
        path: 'patrol-view/:id',
        redirectTo: 'patrol/view/:id',
        pathMatch: 'full',
      },
      {
        path: 'create-patrol/:id',
        redirectTo: 'patrol/create-patrol/:id',
        pathMatch: 'full',
      },

      {
        path: 'analitycs',
        canActivate: [authGuard],
        loadChildren: () =>
          import('./app/features/analitycs/analitycs.routes').then(
            (m) => m.analitycsRoutes,
          ),
      },

      {
        path: 'integrations-gov',
        canActivate: [authGuard, IntegrationsGovGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/integrations-gov-list/integrations-gov-list.component').then(
            (c) => c.IntegrationsGovListComponent,
          ),
        data: {
          breadcrumb: 'Projetos Forças de Segurança',
          menuPermission: 'can_see_security_forces_menu',
        },
        resolve: { me: MeResolver },
      },
      {
        path: 'integrations-moni',
        canActivate: [authGuard, MoniIntegrationGuard],
        loadComponent: () =>
          import('./app/features/integrations-moni/integrations-moni.component').then(
            (c) => c.IntegrationsMoniComponent,
          ),
        data: { breadcrumb: 'Integração Moni' },
        resolve: { me: MeResolver },
      },

      {
        path: 'camera/:cameraId',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./app/features/camera-view/camera-view.component').then(
            (c) => c.CameraViewComponent,
          ),
        data: { breadcrumb: 'Nome da Câmera' },
        resolve: { me: MeResolver },
      },
      {
        path: 'meus-videos',
        canActivate: [authGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/my-videos/my-videos.component').then(
            (c) => c.MyVideosComponent,
          ),
        data: {
          breadcrumb: 'Meus Vídeos',
          menuPermission: 'can_see_camera_menu',
        },
        resolve: { me: MeResolver },
      },
      {
        path: 'lpr-dashboard',
        canActivate: [authGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/lpr-dashboard/lpr-dashboard.component').then(
            (c) => c.LprDashboardComponent,
          ),
        data: { breadcrumb: 'Dashboard', menuPermission: 'can_see_lpr_menu' },
        resolve: { me: MeResolver },
      },
      {
        path: 'lpr',
        canActivate: [authGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/lpr/lpr.component').then(
            (c) => c.LprComponent,
          ),
        data: { breadcrumb: 'LPR', menuPermission: 'can_see_lpr_menu' },
        resolve: {
          me: MeResolver,
        },
      },
      {
        path: 'lpr-blacklist-groups',
        canActivate: [authGuard, LprBlacklistGuard, MenuPermissionGuard],
        loadComponent: () =>
          import('./app/features/lpr-blacklist-groups/lpr-blacklist-groups.component').then(
            (c) => c.LprBlacklistGroupsComponent,
          ),
        data: {
          breadcrumb: 'Monitoramento Placas e Modelos',
          menuPermission: 'can_see_lpr_menu',
        },
        resolve: { me: MeResolver },
      },
    ],
  },

  {
    path: 'invite/:slug/:whitelabelId',
    loadComponent: () =>
      import('./app/auth/components/invite-registration/invite-registration.component').then(
        (c) => c.InviteRegistrationComponent,
      ),
  },
  {
    path: 'auth/confirm-user-self-registration',
    loadComponent: () =>
      import('./app/auth/components/confirm-user-self-registration/confirm-user-self-registration.component').then(
        (c) => c.ConfirmUserSelfRegistrationComponent,
      ),
  },
  { path: 'logout', canActivate: [logoutGuard], children: [] },
  { path: 'notfound', component: Notfound },
  { path: 'auth', component: AuthLayout, children: [] },
  { path: '**', redirectTo: '/notfound' },
];
